import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MagnifyingGlass, Funnel, Package } from '@phosphor-icons/react';
import { useTheme } from '../contexts/ThemeContext';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductsPage() {
    const { theme } = useTheme();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [marqueFilter, setMarqueFilter] = useState('all');

    useEffect(() => {
        fetchProducts();
    }, [search, marqueFilter]);

    const fetchProducts = async () => {
        try {
            setError(null);
            const params = new URLSearchParams();
            if (search) params.append('search', search);
            if (marqueFilter && marqueFilter !== 'all') params.append('marque', marqueFilter);
            const response = await axios.get(`${API}/products?${params}`);
            setProducts(response.data || []);
        } catch (err) {
            console.error('Error fetching products:', err);
            setError('Erreur lors du chargement des produits');
            setProducts([]);
        } finally {
            setLoading(false);
        }
    };

    const uniqueMarques = [...new Set(products.filter(p => p && p.marque && p.marque.trim() !== '').map(p => p.marque))];

    const formatPrice = (price) => {
        if (price === null || price === undefined || isNaN(price)) return 'Prix sur demande';
        try {
            return new Intl.NumberFormat('fr-SN', { style: 'currency', currency: 'XOF' }).format(price);
        } catch {
            return `${price} FCFA`;
        }
    };

    return (
        <div data-testid="products-page" className="min-h-screen">
            {/* Hero Banner */}
            <section className={`py-16 ${theme === 'dark' ? 'bg-mmd-surface-dark' : 'bg-mmd-light-bg'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center"
                    >
                        <p className={`text-sm font-medium tracking-[0.2em] uppercase mb-4 ${theme === 'dark' ? 'text-mmd-gold' : 'text-mmd-navy'}`}>
                            MMD Global
                        </p>
                        <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tighter mb-4">
                            Matériel de Bureau
                        </h1>
                        <p className={`max-w-xl mx-auto ${theme === 'dark' ? 'text-mmd-silver/80' : 'text-mmd-navy/70'}`}>
                            Découvrez notre sélection de matériel de bureau professionnel de haute qualité.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Filters */}
            <section className={`py-6 border-b ${theme === 'dark' ? 'bg-mmd-dark-bg border-white/10' : 'bg-white border-black/10'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                        <div className="relative w-full sm:w-80">
                            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                            <Input
                                placeholder="Rechercher un produit..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-10"
                                data-testid="product-search"
                            />
                        </div>
                        <div className="flex items-center gap-3 w-full sm:w-auto">
                            <Funnel size={18} className="text-muted-foreground" />
                            <Select value={marqueFilter} onValueChange={setMarqueFilter}>
                                <SelectTrigger className="w-full sm:w-48" data-testid="marque-filter">
                                    <SelectValue placeholder="Toutes les marques" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Toutes les marques</SelectItem>
                                    {uniqueMarques.map(marque => (
                                        <SelectItem key={marque} value={marque}>{marque}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>
            </section>

            {/* Products Grid */}
            <section className={`py-12 ${theme === 'dark' ? 'bg-mmd-dark-bg' : 'bg-mmd-light-bg'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {error ? (
                        <div className="text-center py-20">
                            <Package size={64} className="mx-auto mb-4 text-red-500" />
                            <h3 className="font-heading text-xl font-medium mb-2 text-red-500">{error}</h3>
                            <button 
                                onClick={fetchProducts}
                                className={`mt-4 px-4 py-2 rounded-lg ${theme === 'dark' ? 'bg-mmd-gold text-mmd-dark-bg' : 'bg-mmd-navy text-white'}`}
                            >
                                Réessayer
                            </button>
                        </div>
                    ) : loading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[1, 2, 3, 4, 5, 6].map(i => (
                                <div key={i} className={`rounded-xl overflow-hidden ${theme === 'dark' ? 'bg-mmd-surface-dark' : 'bg-white'}`}>
                                    <div className="aspect-[4/3] bg-muted animate-pulse" />
                                    <div className="p-4 space-y-3">
                                        <div className="h-4 bg-muted rounded animate-pulse w-1/4" />
                                        <div className="h-6 bg-muted rounded animate-pulse w-3/4" />
                                        <div className="h-5 bg-muted rounded animate-pulse w-1/3" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : products.length === 0 ? (
                        <div className="text-center py-20">
                            <Package size={64} className="mx-auto mb-4 text-muted-foreground" />
                            <h3 className="font-heading text-xl font-medium mb-2">Aucun produit trouvé</h3>
                            <p className="text-muted-foreground">Essayez de modifier vos critères de recherche.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {products.map((product, index) => {
                                if (!product || !product.id) return null;
                                return (
                                    <motion.div
                                        key={product.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: Math.min(index * 0.05, 0.5) }}
                                    >
                                        <Link
                                            to={`/materiel/${product.id}`}
                                            data-testid={`product-card-${product.id}`}
                                            className={`block rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                                                theme === 'dark' ? 'bg-mmd-surface-dark border border-white/10' : 'bg-white border border-black/5'
                                            }`}
                                        >
                                            <div className="aspect-[4/3] overflow-hidden">
                                                <img
                                                    src={product.images?.[0] || 'https://via.placeholder.com/400x300?text=No+Image'}
                                                    alt={product.nom || 'Produit'}
                                                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                                                    loading="lazy"
                                                    onError={(e) => {
                                                        e.target.src = 'https://via.placeholder.com/400x300?text=Image+Error';
                                                    }}
                                                />
                                            </div>
                                            <div className="p-4">
                                                <p className={`text-xs font-medium uppercase tracking-wide mb-1 ${theme === 'dark' ? 'text-mmd-gold' : 'text-mmd-navy'}`}>
                                                    {product.marque || 'Marque'}
                                                </p>
                                                <h3 className="font-heading font-semibold text-lg mb-2 line-clamp-1">
                                                    {product.nom || 'Produit'}
                                                </h3>
                                                <div className="flex items-center justify-between">
                                                    <span className={`font-semibold ${theme === 'dark' ? 'text-mmd-gold' : 'text-mmd-navy'}`}>
                                                        {formatPrice(product.prix)}
                                                    </span>
                                                    <span className={`text-xs px-2 py-1 rounded-full ${
                                                        (product.stock || 0) > 0 
                                                            ? 'bg-green-500/20 text-green-500' 
                                                            : 'bg-red-500/20 text-red-500'
                                                    }`}>
                                                        {(product.stock || 0) > 0 ? 'En stock' : 'Rupture'}
                                                    </span>
                                                </div>
                                            </div>
                                        </Link>
                                    </motion.div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
